/* eslint-disable no-magic-numbers */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/scandipwa
 * @link https://github.com/scandipwa/scandipwa
 */

import ProductQuery from 'Query/Product.query';
import { ProductDispatcher as SourceProductDispatcher } from 'SourceStore/Product/Product.dispatcher';
import { CATEGORY, FBT } from 'Store/LinkedProducts/LinkedProducts.reducer';
import { updateNoMatch } from 'Store/NoMatch/NoMatch.action';
import { updateProductDetails } from 'Store/Product/Product.action';
import { isMobile } from 'Util/Mobile';
import { getIndexedAttributes } from 'Util/Product';
import { checkAdvertPrice } from 'Util/Product/Product';
import { fetchQuery } from 'Util/Request';
import getStore from 'Util/Store';

export const LinkedProductsDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/LinkedProducts/LinkedProducts.dispatcher'
);

export const DESKTOP_PRODUCT_COUNT_SIZE = 40;
export const MOBILE_PRODUCT_COUNT_SIZE = 20;
export const SERIES_PREFIX = '/serie';

/** @namespace Bookland/Store/Product/Dispatcher */
export class ProductDispatcher extends SourceProductDispatcher {
    roundDiscount(product) {
        const clonedProduct = { ...product };

        clonedProduct.price_range.maximum_price.discount.percent_off = Math.ceil(product.price_range.maximum_price.discount.percent_off);
        clonedProduct.price_range.minimum_price.discount.percent_off = Math.ceil(product.price_range.minimum_price.discount.percent_off);

        return clonedProduct;
    }

    getSeries(categories = []) {
        const seriesList = categories.filter(({ url }) => url.includes(SERIES_PREFIX));

        const series = seriesList?.length > 0 ? seriesList?.reduce(
            (a, b) => (a.length > b.length ? a : b)
        ) : [];

        const filteredCategories = categories.filter(({ url }) => !url.includes(SERIES_PREFIX));

        return [series, filteredCategories];
    }

    findLongestBreadcrumbs(categories) {
        const { breadcrumbsCategory = {} } = categories.reduce((acc, category) => {
            const { longestBreadcrumbsLength } = acc;
            const { breadcrumbs } = category;
            const breadcrumbsLength = (breadcrumbs || []).length;

            if (!breadcrumbsLength && longestBreadcrumbsLength !== 0) {
                return acc;
            }

            if (longestBreadcrumbsLength === 0) {
                return { ...acc, breadcrumbsCategory: category };
            }

            if (breadcrumbsLength <= longestBreadcrumbsLength) {
                return acc;
            }

            return {
                breadcrumbsCategory: category,
                longestBreadcrumbsLength: breadcrumbsLength
            };
        }, {
            breadcrumbsCategory: {},
            longestBreadcrumbsLength: 0
        });

        return breadcrumbsCategory;
    }

    getCategoryId(categories) {
        const state = getStore().getState();

        const isFunctionEnabled = state.ConfigReducer?.satisfly_otherincategory_general_enable;

        if (!isFunctionEnabled) {
            return categories[0];
        }

        const filteredCategoryIds = state.ConfigReducer?.satisfly_otherincategory_general_categories_to_skip?.split(',').map((id) => parseInt(id, 10));

        const { category } = categories.reduce((acc, category) => {
            const { longestBreadcrumbs } = acc;

            const { breadcrumbs } = category;
            const breadcrumbsLength = (breadcrumbs || []).length;
            const isCategoryOff = breadcrumbs?.some(({ category_id }) => filteredCategoryIds.indexOf(category_id) !== -1);

            if (isCategoryOff) {
                return acc;
            }

            if (!breadcrumbsLength && longestBreadcrumbs !== 0) {
                return acc;
            }

            if (longestBreadcrumbs === 0) {
                return {
                    ...acc,
                    category,
                    longestBreadcrumbs: breadcrumbsLength
                };
            }

            if (breadcrumbsLength <= longestBreadcrumbs) {
                return acc;
            }

            return {
                category,
                longestBreadcrumbs: breadcrumbsLength
            };
        }, {
            category: {},
            longestBreadcrumbs: 0
        });

        return category;
    }

    onSuccess(data, dispatch) {
        const { products: { items } } = data;

        /**
     * In case there are no items, or item count is
     * smaller then 0 => the product was not found.
     */
        if (!items || items.length <= 0) {
            dispatch(updateNoMatch(true));

            return;
        }

        let [product] = items;

        const categoryForSlider = this.getCategoryId(product.categories);

        const [series, categories] = this.getSeries(product?.categories);

        const updatedProductItems = product?.items?.map((item) => {
            if (!item.product) {
                return item;
            }

            return {
                ...item,
                product: {
                    ...item.product,
                    attributes: getIndexedAttributes(item.product?.attributes)
                }
            };
        });

        product = {
            ...this.roundDiscount(product),
            categories,
            series,
            price_range: checkAdvertPrice(product),
            items: updatedProductItems
        };

        const product_links = items.reduce((links, product) => {
            const { fbt_products, product_links } = product;

            if (fbt_products) {
                fbt_products.forEach(({ sku }, index) => links.push({ position: index + 1, link_type: FBT, linked_product_sku: sku }));
            }

            if (product_links) {
                Object.values(product_links).forEach((item) => {
                    links.push(item);
                });
            }

            return links;
        }, []);

        LinkedProductsDispatcher.then(
            async ({ default: dispatcher }) => {
                const category_product_links = await this.getOthersProductsInCategory(categoryForSlider?.id ?? 0);

                const product_links_with_category_products = [...product_links, ...category_product_links];

                if (product_links_with_category_products.length > 0) {
                    dispatcher.handleData(dispatch, product_links_with_category_products);
                } else {
                    dispatcher.clearLinkedProducts(dispatch);
                }
            }
        );

        dispatch(updateProductDetails(product));
    }

    async getOthersProductsInCategory(categoryId) {
        const size = isMobile.any() ? MOBILE_PRODUCT_COUNT_SIZE : DESKTOP_PRODUCT_COUNT_SIZE;

        const { otherProductsInCategory } = await fetchQuery(ProductQuery.getOtherProductsInCategory(categoryId, size));

        return otherProductsInCategory
            .filter(({ stock_status }) => stock_status !== 'IN_STOCK')
            .map(({ sku: linked_product_sku }, index) => ({ position: index + 1, link_type: CATEGORY, linked_product_sku }));
    }
}

export default new ProductDispatcher();
